// ============================================================
// MaliTechnique.jsx — Service "Mali technique de fusion" (cas pratique n°2)
// Décomposition du mali de fusion (technique / vrai mali) et affectation du
// mali technique aux actifs sous-jacents au prorata des plus-values latentes,
// plafonnée à celles-ci, résidu au fonds commercial — règlement ANC n° 2014-03
// modifié par ANC n° 2015-06 (comptabilisation par composants depuis 2016),
// PCG art. 745-5 et 745-6 ; volet fiscal BOI-IS-FUS-10-50-10 et -20.
// Onglet du Dash, monté sous <FormScope id="mali">.
// Données client 100 % session-only (useSessionState) — rien sur disque.
// Tout le calcul est front (arithmétique pure), aucun appel réseau.
// ============================================================

import React, { useMemo } from "react";
import { P } from "./palette";
import { useSessionState } from "./sessionForm";
import { locOf, fmtEuro, fmtMoney } from "./format";
import { buildMemoPdf, downloadPdf, MemoPaper } from "./memoDoc";
import { inS, lbS, thS, tdS, NumIn, SecT, Metric, WarnLine } from "./caseUi";

// ── Libellés internes bilingues (seul grp.mali vit dans TR d'App.jsx) ──
const T = {
  fr: {
    intro: "Allocation du mali technique de fusion — décomposition entre mali technique (inscrit à l'actif) et vrai mali (charge), affectation aux actifs sous-jacents au prorata des plus-values latentes et dans la limite de celles-ci, quote-part résiduelle au fonds commercial (règlement ANC n° 2014-03 modifié par le règlement ANC n° 2015-06, PCG art. 745-5 et 745-6).",
    s1: "Mali de fusion total", s2: "Actifs porteurs de plus-values latentes",
    s3: "Décomposition du mali", s4: "Affectation du mali technique",
    modeCalc: "Calculé à partir des titres", modeDirect: "Saisie directe",
    vcTitres: "Valeur comptable des titres annulés",
    anc: "Actif net comptable de l'absorbée",
    pct: "Quote-part détenue (%)",
    qpAnc: "Quote-part d'ANC reçue",
    maliTotal: "Mali de fusion total",
    maliDirectLbl: "Mali de fusion total",
    pctBad: "La quote-part détenue doit être comprise entre 0 et 100 %.",
    boni: "L'opération fait ressortir un boni de fusion (la quote-part d'actif net reçue excède la valeur comptable des titres annulés) — l'allocation du mali est sans objet.",
    boniDirect: "Le mali saisi est nul ou négatif — l'allocation du mali est sans objet.",
    actifsHint: "Saisissez la VNC (comptes de l'absorbée) et la valeur réelle : la plus-value latente est calculée (VR − VNC) et reste traçable. Un actif non comptabilisé (fonds commercial, marque…) se saisit avec une VNC de 0. Les moins-values latentes sont signalées et exclues du prorata.",
    colDesign: "Désignation", colNature: "Nature", colVnc: "VNC", colVr: "Valeur réelle",
    colPv: "PV latente", colSuivi: "Règle de suivi", colDuree: "Durée (ans)",
    designPh: "ex. Fonds de commerce",
    natures: { corporel: "Corporel", incorporel: "Incorporel", financier: "Financier", circulant: "Circulant" },
    suivis: { amort: "Amortissable (durée du sous-jacent)", deprec: "Non amortissable — test de dépréciation", sortie: "Suit la sortie de l'actif" },
    dureePh: "ex. 10",
    mvRow: "Moins-value latente — actif non porteur de mali, exclu du prorata",
    delActif: "Retirer cet actif", addActif: "+ Ajouter un actif",
    techModeLbl: "Détermination du mali technique",
    techListe: "Plafonné aux PV latentes identifiées",
    techListeHint: (mt) => `Mali technique = min(mali total ; somme des PV latentes) = ${mt}`,
    techSaisi: "Mali technique saisi (évaluation globale)",
    techSaisiLbl: "Mali technique",
    techSaisiHint: "Évaluation globale (PV latentes comptabilisées ou non, y compris survaleur non rattachable) — le résidu au-delà des PV identifiées est affecté au fonds commercial (PCG art. 745-5).",
    overCap: (m) => `Le mali technique saisi excède le mali de fusion total — il est plafonné au mali total (${m}).`,
    underPV: "Le mali technique saisi est inférieur aux plus-values latentes identifiées (à hauteur du mali total) — détermination incohérente avec la liste des actifs : le mali technique correspond aux PV latentes des actifs, comptabilisés ou non, à hauteur du mali.",
    noPorteurs: "Aucune plus-value latente identifiée — tout le mali constitue un vrai mali, comptabilisé en charge. Ajoutez les actifs porteurs (y compris un fonds commercial non inscrit, VNC 0) pour dégager un mali technique.",
    mTotal: "Mali total", mTech: "Mali technique (à l'actif)", mVrai: "Vrai mali (en charge)", mResidu: "Résidu → fonds commercial",
    sumPv: "Somme des PV latentes",
    vraiMaliNote: "Le vrai mali est comptabilisé en charge du résultat de l'absorbante ; seul le mali technique est inscrit à l'actif, ventilé par nature de sous-jacent (ANC 2015-06).",
    affHint: "Quote-part par actif = mali technique × (PV latente de l'actif ÷ somme des PV latentes), dans la limite de la PV de chaque actif. Les arrondis sont ajustés sur la dernière ligne pour une affectation exacte au centime.",
    colProrata: "Prorata", colQuote: "Quote-part affectée",
    residuRow: "Fonds commercial (quote-part résiduelle — PCG art. 745-5)",
    totalRow: "Total affecté (= mali technique)",
    ventilTitle: "Ventilation par nature de sous-jacent",
    needMali: "Renseignez le mali de fusion total (section 1) pour calculer l'affectation.",
    needActifs: "Renseignez au moins un actif porteur de plus-value latente (section 2).",
    dureeMissing: (n) => `${n === 1 ? "Un actif amortissable" : `${n} actifs amortissables`} sans durée renseignée — la durée du sous-jacent porte la règle d'amortissement de la quote-part.`,
  },
  en: {
    intro: "Allocation of the technical merger loss (mali technique) — split between technical mali (recognised as an asset) and true mali (expense), allocation to underlying assets pro rata to unrealised gains and capped at those gains, residual share to goodwill (ANC regulation No. 2014-03 as amended by ANC No. 2015-06, PCG art. 745-5 and 745-6).",
    s1: "Total merger loss", s2: "Assets bearing unrealised gains",
    s3: "Breakdown of the mali", s4: "Allocation of the technical mali",
    modeCalc: "Computed from the shares", modeDirect: "Direct entry",
    vcTitres: "Book value of the cancelled shares",
    anc: "Book net assets of the absorbed company",
    pct: "Ownership interest (%)",
    qpAnc: "Share of net assets received",
    maliTotal: "Total merger loss",
    maliDirectLbl: "Total merger loss",
    pctBad: "The ownership interest must be between 0 and 100%.",
    boni: "The transaction yields a merger gain (boni) — the share of net assets received exceeds the book value of the cancelled shares; the mali allocation is not applicable.",
    boniDirect: "The mali entered is nil or negative — the mali allocation is not applicable.",
    actifsHint: "Enter the book value (absorbed company's accounts) and the fair value: the unrealised gain is computed (FV − BV) and remains traceable. An unrecognised asset (goodwill, brand…) is entered with a book value of 0. Unrealised losses are flagged and excluded from the pro rata.",
    colDesign: "Description", colNature: "Class", colVnc: "Book value", colVr: "Fair value",
    colPv: "Unrealised gain", colSuivi: "Follow-up rule", colDuree: "Life (yrs)",
    designPh: "e.g. Goodwill",
    natures: { corporel: "Tangible", incorporel: "Intangible", financier: "Financial", circulant: "Current" },
    suivis: { amort: "Amortisable (underlying asset's life)", deprec: "Non-amortisable — impairment test", sortie: "Follows the asset's disposal" },
    dureePh: "e.g. 10",
    mvRow: "Unrealised loss — asset does not carry any mali, excluded from the pro rata",
    delActif: "Remove this asset", addActif: "+ Add an asset",
    techModeLbl: "Determination of the technical mali",
    techListe: "Capped at the identified unrealised gains",
    techListeHint: (mt) => `Technical mali = min(total mali; sum of unrealised gains) = ${mt}`,
    techSaisi: "Technical mali entered (global assessment)",
    techSaisiLbl: "Technical mali",
    techSaisiHint: "Global assessment (unrealised gains whether recognised or not, including unallocable excess value) — the residual beyond identified gains is allocated to goodwill (PCG art. 745-5).",
    overCap: (m) => `The technical mali entered exceeds the total merger loss — it is capped at the total mali (${m}).`,
    underPV: "The technical mali entered is below the identified unrealised gains (up to the total mali) — inconsistent with the asset list: the technical mali corresponds to the unrealised gains on assets, recognised or not, up to the mali.",
    noPorteurs: "No unrealised gain identified — the whole mali is a true mali, expensed. Add the carrying assets (including unrecognised goodwill, book value 0) to evidence a technical mali.",
    mTotal: "Total mali", mTech: "Technical mali (asset)", mVrai: "True mali (expense)", mResidu: "Residual → goodwill",
    sumPv: "Sum of unrealised gains",
    vraiMaliNote: "The true mali is expensed in the absorbing company's income statement; only the technical mali is recognised as an asset, broken down by class of underlying asset (ANC 2015-06).",
    affHint: "Share per asset = technical mali × (asset's unrealised gain ÷ sum of unrealised gains), capped at each asset's gain. Rounding is adjusted on the last row for an allocation exact to the cent.",
    colProrata: "Pro rata", colQuote: "Allocated share",
    residuRow: "Goodwill (residual share — PCG art. 745-5)",
    totalRow: "Total allocated (= technical mali)",
    ventilTitle: "Breakdown by class of underlying asset",
    needMali: "Enter the total merger loss (section 1) to compute the allocation.",
    needActifs: "Enter at least one asset bearing an unrealised gain (section 2).",
    dureeMissing: (n) => `${n === 1 ? "One amortisable asset" : `${n} amortisable assets`} without a life entered — the underlying asset's life drives the amortisation of the allocated share.`,
  },
};

// ══════════════════════════════════════════════════════════════════════════
// MÉMO JUSTIFICATIF (lot 2) — template déterministe : le mémo n'assemble QUE
// les saisies de l'évaluateur (exigence de traçabilité, aucun contenu généré).
// Un modèle de blocs unique (buildMemoModel) alimente l'aperçu écran ET
// l'export PDF (memoDoc.js) : le livrable est exactement ce qui est prévisualisé.
// ══════════════════════════════════════════════════════════════════════════

const MEMO_T = {
  fr: {
    tabCalc: "Calcul et affectation", tabMemo: "Mémo justificatif",
    opTitle: "Identification de l'opération",
    opAbsorbante: "Société absorbante", opAbsorbee: "Société absorbée",
    opPh: "ex. Alpha SA",
    ckTitle: "Complétude du mémo",
    ckOp: "Opération identifiée (absorbante et absorbée)",
    ckMali: "Mali de fusion total déterminé et positif",
    ckPorteur: "Au moins un actif porteur de plus-value latente",
    ckComplets: "Aucun actif laissé incomplet (VNC et valeur réelle saisies — compléter ou supprimer)",
    ckDesign: "Désignation renseignée pour chaque actif porteur",
    ckTechSaisi: "Mali technique saisi renseigné et n'excédant pas le mali total",
    ckDurees: "Durée renseignée pour chaque quote-part amortissable (porte la règle d'amortissement)",
    ckUnderPV: "Le mali technique saisi est inférieur aux PV latentes brutes identifiées — le mémo mentionnera que cette détermination globale relève de l'appréciation de l'évaluateur (déduction éventuelle de passifs non comptabilisés)",
    exportBtn: "Exporter le mémo (PDF)",
    exportBlocked: "Complétez les points signalés ci-dessus pour activer l'export",
    memoTitle: "MÉMORANDUM", memoSub: "Allocation du mali technique de fusion",
    op: (A, B) => `Fusion-absorption de ${B} (absorbée) par ${A} (absorbante)`,
    dateLbl: "Établi le",
    s1: "1. Opération et détermination du mali de fusion",
    s2: "2. Cadre de référence",
    s3: "3. Plus-values latentes identifiées",
    s4: "4. Décomposition du mali",
    s5: "5. Affectation du mali technique",
    s6: "6. Suivi comptable des quote-parts",
    s7: "7. Volet fiscal (documentation)",
    maliCalcule: (vc, qp, pct, anc, m) => `Le mali de fusion résulte de l'annulation des titres de l'absorbée détenus par l'absorbante : valeur comptable des titres annulés de ${vc}, sous déduction de la quote-part de l'actif net comptable de l'absorbée reçue, soit ${qp} (${pct} % d'un actif net comptable de ${anc}). Le mali de fusion total s'établit à ${m}.`,
    maliDirect: (m) => `Le mali de fusion total, déterminé par l'évaluateur, s'établit à ${m}.`,
    cadre: "Conformément au règlement ANC n° 2014-03 modifié par le règlement ANC n° 2015-06 (PCG art. 745-5 et 745-6), le mali de fusion se décompose en un mali technique, inscrit à l'actif du bilan de la société absorbante et affecté aux actifs sous-jacents auxquels il se rapporte, et un vrai mali, comptabilisé en charge du résultat de l'exercice. Depuis 2016 (ANC n° 2015-06), le mali technique est comptabilisé par composants, dans des sous-comptes dédiés de la catégorie d'immobilisation des actifs sous-jacents, et suit les règles d'amortissement et de dépréciation applicables à ceux-ci.",
    pvIntro: (n, s) => `${n === 1 ? "Un actif porteur de plus-values latentes a été identifié" : `${n} actifs porteurs de plus-values latentes ont été identifiés`} dans les comptes de l'absorbée, pour un montant total de ${s}. Les plus-values latentes sont déterminées par différence entre la valeur réelle et la valeur nette comptable de chaque actif, telles que saisies par l'évaluateur.`,
    pvMv: (n, liste) => n === 1
      ? `Un actif présentant une moins-value latente (${liste}) ne porte aucune quote-part de mali et est exclu du prorata d'affectation.`
      : `${n} actifs présentant une moins-value latente (${liste}) ne portent aucune quote-part de mali et sont exclus du prorata d'affectation.`,
    decListe: (m, s, t) => `Le mali technique est déterminé à hauteur des plus-values latentes identifiées, dans la limite du mali de fusion total : min(${m} ; ${s}) = ${t}.`,
    decSaisi: (t) => `Le mali technique a été déterminé globalement par l'évaluateur à ${t}, dans la limite du mali de fusion total (plus-values latentes sur éléments d'actif comptabilisés ou non dans les comptes de l'absorbée, déduction faite, le cas échéant, des passifs non comptabilisés).`,
    decSaisiUnder: (s) => `Ce montant est inférieur à la somme des plus-values latentes brutes identifiées (${s}) ; cette détermination globale relève de l'appréciation de l'évaluateur.`,
    decomposition: (t, v) => `Mali technique inscrit à l'actif : ${t} — Vrai mali comptabilisé en charge du résultat : ${v}.`,
    affMethode: "Le mali technique est affecté aux actifs sous-jacents au prorata des plus-values latentes et dans la limite de celles-ci ; la quote-part résiduelle non affectable est portée au fonds commercial (PCG art. 745-5). Les arrondis sont ajustés sur la dernière ligne afin que la somme des quote-parts soit strictement égale au mali technique.",
    thDesign: "Actif", thNature: "Nature", thVnc: "VNC", thVr: "Valeur réelle", thPv: "PV latente", thProrata: "Prorata", thQuote: "Quote-part", thSuivi: "Suivi",
    plafondMark: "plafond",
    plafondNote: (t, s) => `Le mali technique (${t}) excédant la somme des plus-values latentes identifiées (${s}), la quote-part de chaque actif est plafonnée à sa plus-value latente et non égale au produit du prorata par le mali technique (colonne « Prorata » : plafond atteint) ; l'excédent est affecté au fonds commercial.`,
    natures: { corporel: "Corporel", incorporel: "Incorporel", financier: "Financier", circulant: "Circulant" },
    suiviShort: { amort: (d) => `Amort. ${d} ans`, deprec: "Dépréciation", sortie: "Sortie actif" },
    residuLigne: "Fonds commercial (résidu)",
    totalLigne: "Total (= mali technique)",
    affResidu: (r) => `La quote-part résiduelle de ${r}, excédant les plus-values latentes identifiées, est affectée au fonds commercial en application de l'article 745-5 du PCG ; elle n'est pas amortie et fait l'objet d'un test de dépréciation.`,
    suivi: "Chaque quote-part de mali technique est comptabilisée dans un sous-compte dédié de la catégorie d'immobilisation de l'actif sous-jacent et suit les règles applicables à celui-ci (PCG art. 745-6) : les quote-parts affectées à des actifs amortissables sont amorties sur la durée résiduelle d'utilisation de l'actif sous-jacent ; celles affectées à des actifs non amortissables font l'objet d'un test de dépréciation ; celles affectées à des actifs circulants sont rapportées au résultat lors de la sortie de l'actif.",
    suiviAmort: (liste) => `Quote-parts amortissables : ${liste}.`,
    fiscal: "Sur le plan fiscal, le mali technique suit le régime fiscal des actifs sous-jacents auxquels il est affecté (BOI-IS-FUS-10-50-10 et BOI-IS-FUS-10-50-20). Lorsque la fusion est placée sous le régime spécial de l'article 210 A du CGI, la charge résultant de la dépréciation ou de la sortie d'une quote-part de mali technique n'est pas admise en déduction lorsque l'actif sous-jacent relève d'un régime de plus-values à long terme ou bénéficie de la neutralité fiscale. Le présent mémorandum documente ce cadre sans procéder à aucun calcul d'impôt.",
    mention: "Document établi exclusivement à partir des données saisies par l'évaluateur. Aucune donnée de marché ni aucun contenu n'a été généré automatiquement.",
    footer: "Hard to Value — Mémo mali technique",
  },
  en: {
    tabCalc: "Computation and allocation", tabMemo: "Justification memo",
    opTitle: "Transaction identification",
    opAbsorbante: "Absorbing company", opAbsorbee: "Absorbed company",
    opPh: "e.g. Alpha SA",
    ckTitle: "Memo completeness",
    ckOp: "Transaction identified (absorbing and absorbed companies)",
    ckMali: "Total merger loss determined and positive",
    ckPorteur: "At least one asset bearing an unrealised gain",
    ckComplets: "No asset left incomplete (book value and fair value entered — fill in or remove)",
    ckDesign: "Description entered for every carrying asset",
    ckTechSaisi: "Technical mali entered and not exceeding the total mali",
    ckDurees: "Life entered for every amortisable share (drives the amortisation rule)",
    ckUnderPV: "The technical mali entered is below the gross identified unrealised gains — the memo will state that this global determination is the valuer's judgement (possible deduction of unrecognised liabilities)",
    exportBtn: "Export the memo (PDF)",
    exportBlocked: "Complete the flagged items above to enable the export",
    memoTitle: "MEMORANDUM", memoSub: "Allocation of the technical merger loss",
    op: (A, B) => `Merger-absorption of ${B} (absorbed) by ${A} (absorbing)`,
    dateLbl: "Prepared on",
    s1: "1. Transaction and determination of the merger loss",
    s2: "2. Framework",
    s3: "3. Identified unrealised gains",
    s4: "4. Breakdown of the mali",
    s5: "5. Allocation of the technical mali",
    s6: "6. Accounting follow-up of the allocated shares",
    s7: "7. Tax layer (documentation)",
    maliCalcule: (vc, qp, pct, anc, m) => `The merger loss arises from the cancellation of the absorbed company's shares held by the absorbing company: book value of the cancelled shares of ${vc}, less the share of the absorbed company's book net assets received, i.e. ${qp} (${pct}% of book net assets of ${anc}). The total merger loss amounts to ${m}.`,
    maliDirect: (m) => `The total merger loss, as determined by the valuer, amounts to ${m}.`,
    cadre: "In accordance with ANC regulation No. 2014-03 as amended by ANC regulation No. 2015-06 (PCG art. 745-5 and 745-6), the merger loss is split between a technical mali, recognised as an asset in the absorbing company's balance sheet and allocated to the underlying assets to which it relates, and a true mali, expensed in the income statement. Since 2016 (ANC No. 2015-06), the technical mali is accounted for by components, in dedicated sub-accounts of the underlying assets' fixed-asset category, and follows their amortisation and impairment rules.",
    pvIntro: (n, s) => `${n === 1 ? "One asset bearing unrealised gains was identified" : `${n} assets bearing unrealised gains were identified`} in the absorbed company's accounts, for a total of ${s}. Unrealised gains are computed as the difference between the fair value and the net book value of each asset, as entered by the valuer.`,
    pvMv: (n, liste) => n === 1
      ? `One asset showing an unrealised loss (${liste}) carries no share of the mali and is excluded from the allocation pro rata.`
      : `${n} assets showing an unrealised loss (${liste}) carry no share of the mali and are excluded from the allocation pro rata.`,
    decListe: (m, s, t) => `The technical mali is determined up to the identified unrealised gains, capped at the total merger loss: min(${m}; ${s}) = ${t}.`,
    decSaisi: (t) => `The technical mali was determined globally by the valuer at ${t}, capped at the total merger loss (unrealised gains on assets whether or not recognised in the absorbed company's accounts, less unrecognised liabilities where applicable).`,
    decSaisiUnder: (s) => `This amount is below the sum of the gross identified unrealised gains (${s}); this global determination is a matter of the valuer's judgement.`,
    decomposition: (t, v) => `Technical mali recognised as an asset: ${t} — True mali expensed in the income statement: ${v}.`,
    affMethode: "The technical mali is allocated to the underlying assets pro rata to their unrealised gains and capped at those gains; the residual unallocable share is recognised as goodwill (PCG art. 745-5). Rounding is adjusted on the last row so that the sum of the allocated shares is strictly equal to the technical mali.",
    thDesign: "Asset", thNature: "Class", thVnc: "Book value", thVr: "Fair value", thPv: "Unreal. gain", thProrata: "Pro rata", thQuote: "Share", thSuivi: "Follow-up",
    plafondMark: "capped",
    plafondNote: (t, s) => `As the technical mali (${t}) exceeds the sum of the identified unrealised gains (${s}), each asset's share is capped at its unrealised gain rather than equal to the pro rata times the technical mali ("Pro rata" column: cap reached); the excess is allocated to goodwill.`,
    natures: { corporel: "Tangible", incorporel: "Intangible", financier: "Financial", circulant: "Current" },
    suiviShort: { amort: (d) => `Amort. ${d} yrs`, deprec: "Impairment", sortie: "On disposal" },
    residuLigne: "Goodwill (residual)",
    totalLigne: "Total (= technical mali)",
    affResidu: (r) => `The residual share of ${r}, exceeding the identified unrealised gains, is allocated to goodwill pursuant to article 745-5 of the PCG; it is not amortised and is subject to an impairment test.`,
    suivi: "Each share of the technical mali is recognised in a dedicated sub-account of the underlying asset's fixed-asset category and follows the rules applicable to that asset (PCG art. 745-6): shares allocated to amortisable assets are amortised over the underlying asset's remaining useful life; shares allocated to non-amortisable assets are subject to an impairment test; shares allocated to current assets are released to profit or loss upon the asset's disposal.",
    suiviAmort: (liste) => `Amortisable shares: ${liste}.`,
    fiscal: "For tax purposes, the technical mali follows the tax regime of the underlying assets to which it is allocated (BOI-IS-FUS-10-50-10 and BOI-IS-FUS-10-50-20). Where the merger is placed under the special regime of article 210 A of the French Tax Code, the expense arising from the impairment or disposal of a share of the technical mali is not tax-deductible where the underlying asset falls under a long-term capital-gains regime or benefits from tax neutrality. This memorandum documents this framework without performing any tax computation.",
    mention: "Document prepared exclusively from the data entered by the valuer. No market data or content was generated automatically.",
    footer: "Hard to Value — Technical mali memo",
  },
};

// ── Modèle de blocs du mémo — source unique pour l'aperçu et le PDF ──
// Registre du livrable : montants EXACTS partout (fmtEuro), jamais de notation
// abrégée k€/M€ — le mémo est annexé au traité de fusion (document opposable).
const buildMemoModel = (d, M, loc, dateStr) => {
  const eur = (v) => fmtEuro(v, loc);
  const share = (v) => (v == null || !isFinite(v) ? "—" : (v * 100).toLocaleString(loc, { maximumFractionDigits: 1 }) + " %");
  const suiviS = (r) => (r.suivi === "amort" ? M.suiviShort.amort(r.duree) : M.suiviShort[r.suivi]);
  const b = [];
  b.push({ type: "head", title: M.memoTitle, sub: M.memoSub, op: M.op(d.opA, d.opB), date: `${M.dateLbl} ${dateStr}` });
  b.push({ type: "h", text: M.s1 });
  b.push({ type: "p", text: M.op(d.opA, d.opB) + "." });
  b.push({ type: "p", text: d.maliMode === "calc"
    ? M.maliCalcule(eur(d.vcTitres), eur(d.qpAnc), d.pct.toLocaleString(loc), eur(d.anc), eur(d.maliTotal))
    : M.maliDirect(eur(d.maliTotal)) });
  b.push({ type: "h", text: M.s2 });
  b.push({ type: "p", text: M.cadre });
  b.push({ type: "h", text: M.s3 });
  b.push({ type: "p", text: M.pvIntro(d.porteurs.length, eur(d.sumPV)) });
  if (d.mvList.length) b.push({ type: "p", text: "— " + M.pvMv(d.mvList.length, d.mvList.join(", ")) });
  b.push({ type: "h", text: M.s4 });
  b.push({ type: "p", text: d.techMode === "liste"
    ? M.decListe(eur(d.maliTotal), eur(d.sumPV), eur(d.maliTech))
    : M.decSaisi(eur(d.maliTech)) });
  if (d.techMode === "saisi" && d.underPV) b.push({ type: "p", text: M.decSaisiUnder(eur(d.sumPV)) });
  b.push({ type: "p", strong: true, text: M.decomposition(eur(d.maliTech), eur(d.vraiMali)) });
  b.push({ type: "h", text: M.s5 });
  b.push({ type: "p", text: M.affMethode });
  // Plafonnement (mali technique > Σ PV) : le prorata ne détermine plus les
  // quote-parts — chaque actif reçoit sa PV entière. La colonne « Prorata »
  // afficherait un produit qui ne tombe pas juste : on y signale « plafond »
  // et une note sous le tableau explicite le calcul (document opposable).
  const capped = d.residuFC > 0;
  const rows = d.porteurs.map((r) => [
    r.designation, M.natures[r.nature], eur(r.vnc), eur(r.vr), eur(r.pv),
    capped ? M.plafondMark : share(d.sumPV > 0 ? r.pv / d.sumPV : null), eur(r.quote), suiviS(r),
  ]);
  if (capped) rows.push([M.residuLigne, M.natures.incorporel, "—", "—", "—", "—", eur(d.residuFC), M.suiviShort.deprec]);
  rows.push([M.totalLigne, "", "", "", "", "", eur(d.maliTech), ""]);
  b.push({
    type: "table",
    cols: [106, 44, 60, 60, 60, 34, 64, 71],
    align: ["l", "l", "r", "r", "r", "r", "r", "l"],
    strongCol: 6,
    clip: [25, 10, 14, 14, 14, 8, 15, 16],
    head: [M.thDesign, M.thNature, M.thVnc, M.thVr, M.thPv, M.thProrata, M.thQuote, M.thSuivi],
    rows,
  });
  if (capped) b.push({ type: "note", text: M.plafondNote(eur(d.maliTech), eur(d.sumPV)) });
  if (capped) b.push({ type: "p", text: M.affResidu(eur(d.residuFC)) });
  b.push({ type: "h", text: M.s6 });
  b.push({ type: "p", text: M.suivi });
  const amorts = d.porteurs.filter((r) => r.suivi === "amort");
  if (amorts.length) b.push({ type: "note", text: M.suiviAmort(amorts.map((r) => `${r.designation} — ${r.duree} ${loc === "fr-FR" ? "ans" : "yrs"}`).join(" · ")) });
  b.push({ type: "h", text: M.s7 });
  b.push({ type: "p", text: M.fiscal });
  b.push({ type: "note", text: M.mention });
  return b;
};

const EPS = 1e-9;
const round2 = (v) => Math.round(v * 100) / 100;
const NATURES = ["corporel", "incorporel", "financier", "circulant"];
// Règle de suivi par défaut selon la nature (modifiable par actif) : un
// corporel s'amortit sur la durée résiduelle du sous-jacent, un incorporel
// type fonds commercial et un financier se testent en dépréciation, un
// circulant est repris en résultat à la sortie de l'actif (ANC 2015-06).
const SUIVI_DEFAULT = { corporel: "amort", incorporel: "deprec", financier: "deprec", circulant: "sortie" };
const newActif = () => ({ id: "a" + Math.random().toString(36).slice(2, 8), designation: "", nature: "corporel", vnc: null, vr: null, suivi: "amort", duree: null });

const MaliTechnique = ({ lang }) => {
  const fr = lang === "fr";
  const t = T[fr ? "fr" : "en"];
  const loc = locOf(lang);

  // État de session (scope "mali") — toutes les saisies client survivent à
  // la navigation, jamais au rechargement, jamais sur disque.
  const [maliMode, setMaliMode] = useSessionState("maliMode", "calc");
  const [maliCalc, setMaliCalc] = useSessionState("maliCalc", { vcTitres: null, anc: null, pct: null });
  const [maliDirect, setMaliDirect] = useSessionState("maliDirect", null);
  const [techMode, setTechMode] = useSessionState("techMode", "liste");
  const [techSaisi, setTechSaisi] = useSessionState("techSaisi", null);
  const [actifs, setActifs] = useSessionState("actifs", () => [newActif()]);
  const [op, setOp] = useSessionState("operation", { absorbante: "", absorbee: "" });
  const [mTab, setMTab] = useSessionState("mTab", "calc");

  const uCalc = (k) => (v) => setMaliCalc((p) => ({ ...p, [k]: v }));
  const uActif = (id, k) => (v) => setActifs((p) => p.map((a) => (a.id === id ? { ...a, [k]: v } : a)));
  // Changer la nature réaligne la règle de suivi sur son défaut (l'utilisateur
  // peut ensuite la modifier) ; la durée n'a de sens qu'en amortissable.
  const uNature = (id) => (nature) => setActifs((p) => p.map((a) => (a.id === id ? { ...a, nature, suivi: SUIVI_DEFAULT[nature], duree: SUIVI_DEFAULT[nature] === "amort" ? a.duree : null } : a)));
  const uSuivi = (id) => (suivi) => setActifs((p) => p.map((a) => (a.id === id ? { ...a, suivi, duree: suivi === "amort" ? a.duree : null } : a)));
  const delActif = (id) => setActifs((p) => p.filter((a) => a.id !== id));
  const addActif = () => setActifs((p) => [...p, newActif()]);

  // ── Mali total ──
  const pctBad = maliCalc.pct != null && (maliCalc.pct <= 0 || maliCalc.pct > 100);
  const qpAnc = maliCalc.anc != null && maliCalc.pct != null && !pctBad ? maliCalc.anc * maliCalc.pct / 100 : null;
  const maliTotal = maliMode === "calc"
    ? (maliCalc.vcTitres != null && qpAnc != null ? maliCalc.vcTitres - qpAnc : null)
    : maliDirect;
  const boni = maliTotal != null && maliTotal <= EPS;
  const maliOk = maliTotal != null && maliTotal > EPS;

  // ── PV latentes par actif ──
  const rows = useMemo(() => actifs.map((a) => {
    const pv = a.vnc != null && a.vr != null ? a.vr - a.vnc : null;
    return { ...a, pv, porteur: pv != null && pv > EPS, mv: pv != null && pv < -EPS };
  }), [actifs]);
  const porteurs = useMemo(() => rows.filter((r) => r.porteur), [rows]);
  const sumPV = useMemo(() => porteurs.reduce((s, r) => s + r.pv, 0), [porteurs]);

  // ── Décomposition : mali technique / vrai mali / résidu fonds commercial ──
  // Mode "liste" : technique = min(mali total, Σ PV identifiées) — résidu nul
  // par construction. Mode "saisi" : technique global saisi, plafonné au mali
  // total ; l'excédent sur les PV identifiées va au fonds commercial (745-5).
  const overCap = techMode === "saisi" && techSaisi != null && maliOk && techSaisi > maliTotal + EPS;
  const maliTech = !maliOk ? null
    : techMode === "liste" ? Math.min(maliTotal, sumPV)
    : techSaisi != null ? Math.min(techSaisi, maliTotal) : null;
  const underPV = techMode === "saisi" && maliTech != null && maliTech < Math.min(sumPV, maliTotal) - 0.005;
  const vraiMali = maliOk && maliTech != null ? maliTotal - maliTech : null;
  // Affectation aux actifs identifiés, plafonnée à leurs PV ; le solde du
  // mali technique non affectable est le résidu fonds commercial.
  const allocBase = maliTech != null ? Math.min(maliTech, sumPV) : null;
  const residuFC = maliTech != null ? round2(Math.max(maliTech - sumPV, 0)) : null;

  // ── Quote-parts au prorata, exactes au centime (ajustement dernière ligne) ──
  const quotes = useMemo(() => {
    if (allocBase == null || sumPV <= EPS) return {};
    const q = {};
    let cum = 0;
    porteurs.forEach((r, i) => {
      if (i === porteurs.length - 1) q[r.id] = round2(allocBase - cum);
      else { q[r.id] = round2(allocBase * r.pv / sumPV); cum += q[r.id]; }
    });
    return q;
  }, [allocBase, sumPV, porteurs]);

  // ── Ventilation par nature (quote-parts + résidu rattaché à l'incorporel) ──
  const ventil = useMemo(() => {
    const v = {};
    porteurs.forEach((r) => { if (quotes[r.id] != null) v[r.nature] = (v[r.nature] || 0) + quotes[r.id]; });
    return v;
  }, [porteurs, quotes]);

  const dureesManquantes = porteurs.filter((r) => r.suivi === "amort" && !(r.duree > 0)).length;
  const suiviLabel = (r) => r.suivi === "amort" && r.duree > 0
    ? (fr ? `Amortissable — ${r.duree} ans` : `Amortisable — ${r.duree} yrs`)
    : t.suivis[r.suivi];
  const fmtShare = (v) => (v == null || !isFinite(v) ? "—" : (v * 100).toLocaleString(loc, { maximumFractionDigits: 1 }) + " %");

  // ── Mémo justificatif (lot 2) : checklist bloquante + modèle de blocs ──
  // Bloquant : tout actif à moitié saisi disparaîtrait silencieusement du
  // tableau annexé, et une quote-part amortissable sans durée priverait la
  // règle de suivi de sa substance — l'évaluateur complète ou supprime.
  const ML = MEMO_T[fr ? "fr" : "en"];
  const incomplets = rows.filter((r) => r.pv == null).length;
  const mvList = rows.filter((r) => r.mv).map((r) => r.designation || "—");
  const checklist = [
    { ok: !!((op.absorbante || "").trim() && (op.absorbee || "").trim()), label: ML.ckOp },
    { ok: maliOk, label: ML.ckMali },
    { ok: porteurs.length >= 1, label: ML.ckPorteur },
    { ok: incomplets === 0, label: ML.ckComplets },
    { ok: porteurs.every((r) => (r.designation || "").trim()), label: ML.ckDesign },
    ...(techMode === "saisi" ? [{ ok: techSaisi != null && !overCap, label: ML.ckTechSaisi }] : []),
    { ok: dureesManquantes === 0, label: ML.ckDurees },
  ];
  const memoReady = checklist.every((c) => c.ok);
  const memoBlocks = useMemo(() => {
    if (!memoReady) return null;
    return buildMemoModel(
      {
        opA: op.absorbante.trim(), opB: op.absorbee.trim(),
        maliMode, vcTitres: maliCalc.vcTitres, anc: maliCalc.anc, pct: maliCalc.pct, qpAnc,
        maliTotal, techMode, maliTech, vraiMali, sumPV, underPV, residuFC, mvList,
        porteurs: porteurs.map((r) => ({ ...r, designation: (r.designation || "").trim(), quote: quotes[r.id] })),
      },
      ML, loc, new Date().toLocaleDateString(loc)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoReady, op, maliMode, maliCalc, qpAnc, maliTotal, techMode, maliTech, vraiMali, sumPV, underPV, residuFC, porteurs, quotes, loc]);
  const exportMemo = () => {
    if (!memoBlocks) return;
    const nm = (s, dflt) => ((s || dflt).replace(/\s+/g, "_").replace(/[^\w-]/g, ""));
    downloadPdf(buildMemoPdf(memoBlocks, ML.footer), `HardToValue_Mali_${nm(op.absorbante, "A")}_${nm(op.absorbee, "B")}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const tabBtn = (id, label) => (
    <button onClick={() => setMTab(id)} style={{
      display: "flex", alignItems: "center", gap: 7, padding: "8px 16px",
      background: mTab === id ? "rgba(255,255,255,0.06)" : "transparent",
      border: "1px solid " + (mTab === id ? P.accentBorder : "rgba(255,255,255,0.08)"),
      borderRadius: 9, color: mTab === id ? P.accent : P.textMuted,
      fontSize: 12.5, fontWeight: mTab === id ? 700 : 500, cursor: "pointer",
    }}>{label}</button>
  );

  const g4 = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 };
  const modeBtn = (active, label, onClick) => (
    <button onClick={onClick} style={{
      padding: "7px 14px", borderRadius: 8, fontSize: 12, cursor: "pointer",
      background: active ? P.accentSoft : "rgba(255,255,255,0.04)",
      border: "1px solid " + (active ? P.accentBorder : "rgba(255,255,255,0.1)"),
      color: active ? P.accent : P.textMuted, fontWeight: active ? 700 : 500,
    }}>{label}</button>
  );

  return (
    <div>
      <p style={{ fontSize: 12.5, color: P.textMuted, lineHeight: 1.6, margin: "0 0 14px", maxWidth: 860 }}>{t.intro}</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
        {tabBtn("calc", "🧮 " + ML.tabCalc)}
        {tabBtn("memo", "📄 " + ML.tabMemo)}
      </div>

      {mTab === "calc" && (<>

      {/* ── 1. Mali total ── */}
      <SecT n="01" t={t.s1} />
      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {modeBtn(maliMode === "calc", t.modeCalc, () => setMaliMode("calc"))}
          {modeBtn(maliMode === "direct", t.modeDirect, () => setMaliMode("direct"))}
        </div>
        {maliMode === "calc" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10 }}>
            <div><label style={lbS}>{t.vcTitres}</label><NumIn v={maliCalc.vcTitres} oc={uCalc("vcTitres")} fmt={(v) => fmtMoney(v, loc)} ph="ex. 1,2M" /></div>
            <div><label style={lbS}>{t.anc}</label><NumIn v={maliCalc.anc} oc={uCalc("anc")} fmt={(v) => fmtMoney(v, loc)} ph="ex. 900k" /></div>
            <div><label style={lbS}>{t.pct}</label><NumIn v={maliCalc.pct} oc={uCalc("pct")} fmt={(v) => String(v).replace(".", fr ? "," : ".")} ph="ex. 100" /></div>
            <Metric lb={t.qpAnc} v={fmtMoney(qpAnc, loc)} a="#64748b" />
            <Metric lb={t.maliTotal} v={fmtMoney(maliTotal, loc)} a={maliOk ? P.accent : "#64748b"} />
          </div>
        ) : (
          <div style={{ maxWidth: 240 }}>
            <label style={lbS}>{t.maliDirectLbl}</label>
            <NumIn v={maliDirect} oc={setMaliDirect} fmt={(v) => fmtMoney(v, loc)} ph="ex. 30 000" style={{ fontSize: 15, padding: "9px 10px" }} />
          </div>
        )}
        {pctBad && <WarnLine tone="err">{t.pctBad}</WarnLine>}
        {boni && <WarnLine tone="err">{maliMode === "calc" ? t.boni : t.boniDirect}</WarnLine>}
      </div>

      {/* ── 2. Actifs porteurs ── */}
      <SecT n="02" t={t.s2} />
      <p style={{ fontSize: 11.5, color: P.textMuted, margin: "0 0 10px", lineHeight: 1.5 }}>{t.actifsHint}</p>
      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "8px 12px 12px", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ ...thS, textAlign: "left", width: "20%" }}>{t.colDesign}</th>
              <th style={{ ...thS, textAlign: "left", width: "11%" }}>{t.colNature}</th>
              <th style={{ ...thS, width: "12%" }}>{t.colVnc}</th>
              <th style={{ ...thS, width: "12%" }}>{t.colVr}</th>
              <th style={{ ...thS, width: "12%" }}>{t.colPv}</th>
              <th style={{ ...thS, textAlign: "left", width: "20%" }}>{t.colSuivi}</th>
              <th style={{ ...thS, width: "8%" }}>{t.colDuree}</th>
              <th style={{ ...thS, width: "5%" }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr>
                  <td style={{ ...tdS, textAlign: "left" }}>
                    <input value={r.designation} placeholder={t.designPh} onChange={(e) => uActif(r.id, "designation")(e.target.value)} style={{ ...inS, fontFamily: "inherit", fontSize: 12.5 }} />
                  </td>
                  <td style={{ ...tdS, textAlign: "left" }}>
                    <select value={r.nature} onChange={(e) => uNature(r.id)(e.target.value)} style={{ ...inS, fontFamily: "inherit", fontSize: 11.5, cursor: "pointer" }}>
                      {NATURES.map((n) => <option key={n} value={n}>{t.natures[n]}</option>)}
                    </select>
                  </td>
                  <td style={tdS}><NumIn v={r.vnc} oc={uActif(r.id, "vnc")} fmt={(v) => fmtMoney(v, loc)} ph="ex. 0" /></td>
                  <td style={tdS}><NumIn v={r.vr} oc={uActif(r.id, "vr")} fmt={(v) => fmtMoney(v, loc)} ph="ex. 100k" /></td>
                  <td style={{ ...tdS, color: r.mv ? "#fbbf24" : P.accent, fontWeight: 700 }}>{r.pv == null ? "—" : fmtMoney(r.pv, loc)}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>
                    <select value={r.suivi} onChange={(e) => uSuivi(r.id)(e.target.value)} style={{ ...inS, fontFamily: "inherit", fontSize: 11.5, cursor: "pointer" }}>
                      {Object.keys(t.suivis).map((s) => <option key={s} value={s}>{t.suivis[s]}</option>)}
                    </select>
                  </td>
                  <td style={tdS}>
                    {r.suivi === "amort"
                      ? <NumIn v={r.duree} oc={uActif(r.id, "duree")} fmt={(v) => String(v)} ph={t.dureePh} />
                      : <span style={{ color: P.textMuted }}>—</span>}
                  </td>
                  <td style={{ ...tdS, whiteSpace: "nowrap" }}>
                    <button onClick={() => delActif(r.id)} title={t.delActif}
                      style={{ background: "transparent", border: "none", color: P.textMuted, fontSize: 13, cursor: "pointer", padding: 2 }}>✕</button>
                  </td>
                </tr>
                {r.mv && (
                  <tr>
                    <td colSpan={8} style={{ padding: "0 8px 8px", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <div style={{ fontSize: 11, color: "#fbbf24" }}>{t.mvRow}</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        <button onClick={addActif} style={{ marginTop: 10, background: "rgba(255,255,255,0.04)", border: "1px dashed rgba(255,255,255,0.15)", borderRadius: 8, padding: "7px 14px", color: P.textSec, fontSize: 12, cursor: "pointer" }}>
          {t.addActif}
        </button>
      </div>

      {/* ── 3. Décomposition ── */}
      <SecT n="03" t={t.s3} />
      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16 }}>
        <label style={lbS}>{t.techModeLbl}</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
          {modeBtn(techMode === "liste", t.techListe, () => setTechMode("liste"))}
          {modeBtn(techMode === "saisi", t.techSaisi, () => setTechMode("saisi"))}
          {techMode === "saisi" && (
            <div style={{ width: 170 }}>
              <NumIn v={techSaisi} oc={setTechSaisi} fmt={(v) => fmtMoney(v, loc)} ph="ex. 30 000" />
            </div>
          )}
        </div>
        {techMode === "liste" && maliTech != null && (
          <p style={{ fontSize: 11.5, color: P.textMuted, margin: "0 0 10px" }}>{t.techListeHint(fmtMoney(maliTech, loc))}</p>
        )}
        {techMode === "saisi" && (
          <p style={{ fontSize: 11.5, color: P.textMuted, margin: "0 0 10px", lineHeight: 1.5 }}>{t.techSaisiHint}</p>
        )}
        {!maliOk && !boni ? (
          <p style={{ fontSize: 12.5, color: P.textMuted, margin: 0 }}>{t.needMali}</p>
        ) : maliOk && (
          <>
            <div style={g4}>
              <Metric lb={t.mTotal} v={fmtMoney(maliTotal, loc)} a="#64748b" />
              <Metric lb={t.mTech} v={fmtMoney(maliTech, loc)} a={P.accent} />
              <Metric lb={t.mVrai} v={fmtMoney(vraiMali, loc)} a="#ef4444" />
              <Metric lb={t.mResidu} v={fmtMoney(residuFC, loc)} a="#8b5cf6" />
            </div>
            <p style={{ fontSize: 11, color: P.textMuted, margin: "10px 0 0", lineHeight: 1.5 }}>{t.vraiMaliNote}</p>
            {overCap && <WarnLine tone="err">{t.overCap(fmtMoney(maliTotal, loc))}</WarnLine>}
            {underPV && <WarnLine>{t.underPV}</WarnLine>}
            {techMode === "liste" && sumPV <= EPS && <WarnLine>{t.noPorteurs}</WarnLine>}
          </>
        )}
      </div>

      {/* ── 4. Affectation ── */}
      <SecT n="04" t={t.s4} />
      {!maliOk ? (
        <p style={{ fontSize: 12.5, color: P.textMuted }}>{t.needMali}</p>
      ) : maliTech == null || (porteurs.length === 0 && !(residuFC > 0)) ? (
        <p style={{ fontSize: 12.5, color: P.textMuted }}>{t.needActifs}</p>
      ) : (
        <>
          <p style={{ fontSize: 11.5, color: P.textMuted, margin: "0 0 10px", lineHeight: 1.5 }}>{t.affHint}</p>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "8px 12px 12px", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={{ ...thS, textAlign: "left" }}>{t.colDesign}</th>
                  <th style={{ ...thS, textAlign: "left" }}>{t.colNature}</th>
                  <th style={thS}>{t.colVnc}</th>
                  <th style={thS}>{t.colVr}</th>
                  <th style={thS}>{t.colPv}</th>
                  <th style={thS}>{t.colProrata}</th>
                  <th style={thS}>{t.colQuote}</th>
                  <th style={{ ...thS, textAlign: "left" }}>{t.colSuivi}</th>
                </tr>
              </thead>
              <tbody>
                {porteurs.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...tdS, textAlign: "left", fontFamily: "inherit" }}>{r.designation || "—"}</td>
                    <td style={{ ...tdS, textAlign: "left", fontFamily: "inherit", color: P.textSec }}>{t.natures[r.nature]}</td>
                    <td style={tdS}>{fmtEuro(r.vnc, loc)}</td>
                    <td style={tdS}>{fmtEuro(r.vr, loc)}</td>
                    <td style={tdS}>{fmtEuro(r.pv, loc)}</td>
                    <td style={{ ...tdS, color: P.textSec }}>{residuFC > 0 ? ML.plafondMark : fmtShare(sumPV > EPS ? r.pv / sumPV : null)}</td>
                    <td style={{ ...tdS, color: P.accent, fontWeight: 700 }}>{fmtEuro(quotes[r.id], loc)}</td>
                    <td style={{ ...tdS, textAlign: "left", fontFamily: "inherit", fontSize: 11, color: P.textSec }}>{suiviLabel(r)}</td>
                  </tr>
                ))}
                {residuFC > 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...tdS, textAlign: "left", fontFamily: "inherit", color: P.textSec, fontStyle: "italic" }}>{t.residuRow}</td>
                    <td style={{ ...tdS, color: "#8b5cf6", fontWeight: 700 }}>{fmtEuro(residuFC, loc)}</td>
                    <td style={{ ...tdS, textAlign: "left", fontFamily: "inherit", fontSize: 11, color: P.textSec }}>{t.suivis.deprec}</td>
                  </tr>
                )}
                <tr>
                  <td colSpan={6} style={{ ...tdS, textAlign: "left", fontFamily: "inherit", fontWeight: 700, borderBottom: "none" }}>{t.totalRow}</td>
                  <td style={{ ...tdS, fontWeight: 700, borderBottom: "none" }}>{fmtEuro(maliTech, loc)}</td>
                  <td style={{ borderBottom: "none" }} />
                </tr>
              </tbody>
            </table>
          </div>
          {residuFC > 0 && (
            <p style={{ fontSize: 11, color: P.textMuted, margin: "8px 2px 0", lineHeight: 1.5 }}>
              {ML.plafondNote(fmtEuro(maliTech, loc), fmtEuro(sumPV, loc))}
            </p>
          )}
          {dureesManquantes > 0 && <WarnLine>{t.dureeMissing(dureesManquantes)}</WarnLine>}

          <div style={{ ...g4, marginTop: 14 }}>
            <Metric lb={t.sumPv} v={fmtMoney(sumPV, loc)} a="#64748b" />
            <Metric lb={t.mTech} v={fmtMoney(maliTech, loc)} a={P.accent} />
            <Metric lb={t.mVrai} v={fmtMoney(vraiMali, loc)} a="#ef4444" />
            {residuFC > 0 && <Metric lb={t.mResidu} v={fmtMoney(residuFC, loc)} a="#8b5cf6" />}
          </div>

          {Object.keys(ventil).length > 0 && (
            <div style={{ marginTop: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: P.textSec, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>{t.ventilTitle}</div>
              {NATURES.filter((n) => ventil[n] != null).map((n) => (
                <div key={n} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, lineHeight: 2, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <span style={{ color: P.textSec }}>{t.natures[n]}</span>
                  <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{fmtEuro(ventil[n], loc)}</span>
                </div>
              ))}
              {residuFC > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, lineHeight: 2 }}>
                  <span style={{ color: P.textSec, fontStyle: "italic" }}>{t.residuRow}</span>
                  <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#8b5cf6" }}>{fmtEuro(residuFC, loc)}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      </>)}

      {mTab === "memo" && (
        <div style={{ marginTop: 18 }}>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: P.textSec, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>{ML.opTitle}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10, marginBottom: 4 }}>
              <div>
                <label style={lbS}>{ML.opAbsorbante}</label>
                <input value={op.absorbante} placeholder={ML.opPh} onChange={(e) => setOp((p) => ({ ...p, absorbante: e.target.value }))} style={{ ...inS, fontFamily: "inherit" }} />
              </div>
              <div>
                <label style={lbS}>{ML.opAbsorbee}</label>
                <input value={op.absorbee} placeholder={ML.opPh} onChange={(e) => setOp((p) => ({ ...p, absorbee: e.target.value }))} style={{ ...inS, fontFamily: "inherit" }} />
              </div>
            </div>
          </div>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: P.textSec, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>{ML.ckTitle}</div>
            {checklist.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, lineHeight: 1.9, color: c.ok ? P.textSec : "#fbbf24" }}>
                <span style={{ color: c.ok ? "#22c55e" : "#fbbf24", fontWeight: 700 }}>{c.ok ? "✓" : "✗"}</span>{c.label}
              </div>
            ))}
            {memoReady && techMode === "saisi" && underPV && <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 8 }}>ⓘ {ML.ckUnderPV}</div>}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
              <button onClick={exportMemo} disabled={!memoReady} style={{
                background: memoReady ? P.accent : "rgba(201,168,76,0.25)", border: "none", borderRadius: 9,
                padding: "10px 18px", color: memoReady ? P.accentText : P.textMuted, fontSize: 13, fontWeight: 700,
                cursor: memoReady ? "pointer" : "not-allowed",
              }}>{ML.exportBtn}</button>
              {!memoReady && <span style={{ fontSize: 11.5, color: P.textMuted }}>{ML.exportBlocked}</span>}
            </div>
          </div>
          {memoBlocks && <MemoPaper blocks={memoBlocks} />}
        </div>
      )}
    </div>
  );
};

export default MaliTechnique;
